import type { StateCreator } from 'zustand';
import type { DateRange } from 'react-day-picker';
import type { Service } from '../../../typings/common/booking';

export interface DateTimeSlice {
	// State
	selectedDate: Date | undefined;
	selectedRange: DateRange | undefined;
	selectedTime: string | undefined;
	selectedDuration: number;

	/**
	 * Tracks whether the store has been initialized from the hidden input value.
	 * This prevents race conditions in multi-page forms where useBookingTimeValue
	 * would compute an empty string and overwrite the hidden input before the
	 * initialize() function could restore the saved state from the hidden input.
	 */
	isInitialized: boolean;

	// Actions
	setSelectedDate: (date: Date | undefined) => void;
	setSelectedRange: (range: DateRange | undefined) => void;
	setSelectedTime: (time: string | undefined) => void;
	setSelectedDuration: (duration: number) => void;
	reset: () => void;

	// Initialization from hidden input
	initialize: (initialValue: string, service: Service) => void;
}

export const createDateTimeSlice: StateCreator<DateTimeSlice> = (set) => ({
	selectedDate: undefined,
	selectedRange: undefined,
	selectedTime: undefined,
	selectedDuration: 0,
	isInitialized: false,

	setSelectedDate: (date) => set({ selectedDate: date, isInitialized: true }),
	setSelectedRange: (range) =>
		set({ selectedRange: range, isInitialized: true }),
	setSelectedTime: (time) => set({ selectedTime: time, isInitialized: true }),
	setSelectedDuration: (duration) =>
		set({ selectedDuration: duration, isInitialized: true }),

	reset: () =>
		set({
			selectedDate: undefined,
			selectedRange: undefined,
			selectedTime: undefined,
			selectedDuration: 0,
			isInitialized: false,
		}),

	initialize: (initialValue: string, service: Service) => {
		if (!service) {
			return;
		}

		if (!initialValue) {
			set({ isInitialized: true });
			return;
		}

		const isDateRange = service.blockSize >= 1440;

		try {
			if (isDateRange && initialValue.includes('|')) {
				// Date range: "2024-03-20 00:00:00|2024-03-22 23:59:59"
				const [start, end] = initialValue.split('|');
				set({
					selectedRange: {
						from: new Date(start),
						to: new Date(end),
					},
					selectedDuration: 0, // Not applicable for date ranges
					isInitialized: true,
				});
			} else if (initialValue.includes('|')) {
				// Duration range: "2024-03-20 14:00:00|2024-03-20 15:30:00"
				const [start, end] = initialValue.split('|');
				const startDate = new Date(start);
				const endDate = new Date(end);

				// Calculate duration in minutes
				const durationMinutes = Math.round(
					(endDate.getTime() - startDate.getTime()) / (1000 * 60)
				);

				set({
					selectedDate: startDate,
					selectedTime: startDate.toTimeString().slice(0, 8),
					selectedDuration: durationMinutes,
					isInitialized: true,
				});
			} else {
				// Single datetime: "2024-03-20 14:00:00"
				const date = new Date(initialValue);
				set({
					selectedDate: date,
					selectedTime: date.toTimeString().slice(0, 8),
					selectedDuration: service.blockSize,
					isInitialized: true,
				});
			}
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(
				'Failed to initialize booking state:',
				initialValue,
				e
			);
		}
	},
});
